// Mirrored from cloud/functions/src/domain/api.ts.
// Single source of truth lives there; keep these in sync until both
// codebases consume a shared @ufh/domain workspace package.

// Dashboard ↔ Functions REST API request/response shapes.
// Source of truth: specs/001-floor-zone-control/contracts/api-rest.md

import { z } from 'zod';
import {
  ReportedSystemMode,
  FloorCall,
  GroupName,
  GroupState,
  WpmMode,
  LinkHealth,
  OverrideKind,
} from './hardware-names';

// --- POST /setpoint -----------------------------------------------------

export const SetSetpointBody = z.object({
  setpoint_c: z.number(),
});
export type SetSetpointBody = z.infer<typeof SetSetpointBody>;

// --- POST /groups/{group}/offset ----------------------------------------

export const SetOffsetBody = z.object({
  offset_c: z.number().min(-5).max(5),
});
export type SetOffsetBody = z.infer<typeof SetOffsetBody>;

// --- POST /groups/{group}/override --------------------------------------

export const ApplyOverrideBody = z.object({
  kind: OverrideKind,
  duration_ms: z
    .number()
    .int()
    .positive()
    .max(24 * 60 * 60 * 1000), // 24h cap per spec.md Assumptions
});
export type ApplyOverrideBody = z.infer<typeof ApplyOverrideBody>;

// --- GET /state ---------------------------------------------------------

export const StateResponse = z.object({
  deviceId: z.string(),
  firmwareVersion: z.string(),      // twin.reported.firmware_version, 'unknown' if missing
  lastReportedAt: z.string(),       // ISO-8601
  systemMode: ReportedSystemMode,   // effective/current mode; 'off' in safe state
  setpoint_c: z.number(),
  // Tolerate a backend that predates this field (rollout window: new frontend
  // vs not-yet-redeployed API) — default to 0 (no offset) when absent.
  wpm_offset_c: z.number().default(0),
  weather_location: z.object({ lat: z.number(), lon: z.number() }).nullable().default(null),
  isStaleConfig: z.boolean(),
  floors: z.object({
    ground: z.object({
      reading_c: z.number(),
      setpoint_effective_c: z.number(),
      call: FloorCall,
      mode: ReportedSystemMode,
      in_safe_state: z.boolean(),
      active_faults: z.array(z.string()),
    }),
    middle: z.object({
      reading_c: z.number(),
      setpoint_effective_c: z.number(),
      call: FloorCall,
      mode: ReportedSystemMode,
      in_safe_state: z.boolean(),
      active_faults: z.array(z.string()),
    }),
    top: z.object({
      reading_c: z.number(),
      setpoint_effective_c: z.number(),
      call: FloorCall,
      mode: ReportedSystemMode,
      in_safe_state: z.boolean(),
      active_faults: z.array(z.string()),
    }),
  }),
  groups: z.object({
    ground: z.object({
      state: GroupState,
      since_ms: z.number().int().nonnegative(),
      offset_c: z.number(),
      active_override: z
        .object({ kind: OverrideKind, expires_at: z.string() })
        .nullable(),
    }),
    middle_sun: z.object({
      state: GroupState,
      since_ms: z.number().int().nonnegative(),
      offset_c: z.number(),
      active_override: z
        .object({ kind: OverrideKind, expires_at: z.string() })
        .nullable(),
    }),
    middle_shade: z.object({
      state: GroupState,
      since_ms: z.number().int().nonnegative(),
      offset_c: z.number(),
      active_override: z
        .object({ kind: OverrideKind, expires_at: z.string() })
        .nullable(),
    }),
    top_sun: z.object({
      state: GroupState,
      since_ms: z.number().int().nonnegative(),
      offset_c: z.number(),
      active_override: z
        .object({ kind: OverrideKind, expires_at: z.string() })
        .nullable(),
    }),
    top_shade: z.object({
      state: GroupState,
      since_ms: z.number().int().nonnegative(),
      offset_c: z.number(),
      active_override: z
        .object({ kind: OverrideKind, expires_at: z.string() })
        .nullable(),
    }),
  }),
  heat_pump: z.object({
    link_health: LinkHealth,
    current_mode: WpmMode,
    operating_mode: z.string(),
    current_flow_temp_c: z.number(),
    outside_temp_c: z.number().default(0),
    hc1_setpoint_c: z.number(),
    fault_bitmap: z.number().int().nonnegative(),
    dewpoint_alarm: z.boolean(),
    summer_mode: z.boolean(),
  }),
  bypass: z.object({ group: GroupName, enabled: z.boolean() }),
  active_faults: z.array(z.string()),
});
export type StateResponse = z.infer<typeof StateResponse>;

// --- Additional wire shapes the dashboard needs ------------------------

export const ListSchedulesResponse = z.object({
  schedules: z.array(z.unknown()), // ScheduleDoc[] — leave loose at API edge; ScheduleDoc parses in domain/schedules
});
export type ListSchedulesResponse = z.infer<typeof ListSchedulesResponse>;

export const ListFaultsResponse = z.object({
  faults: z.array(z.unknown()),
});
export type ListFaultsResponse = z.infer<typeof ListFaultsResponse>;

export const ListAuditResponse = z.object({
  audit: z.array(z.unknown()),
});
export type ListAuditResponse = z.infer<typeof ListAuditResponse>;

export const RollupsResponse = z.object({
  entityKind: z.enum(['floor', 'group', 'weather']),
  entityName: z.string(),
  // Rows kept loose at the API edge (floor rows carry avg/min/max/call,
  // group rows carry openFraction); the Trends page reads fields
  // defensively. Every row has at least a `day`.
  rollups: z.array(z.record(z.string(), z.unknown())),
});
export type RollupsResponse = z.infer<typeof RollupsResponse>;

export const HistoryResponse = z.object({
  entityKind: z.enum(['floor', 'group', 'heat_pump_link', 'controller', 'weather']),
  entityName: z.string(),
  from: z.string(),
  to: z.string(),
  rows: z.array(z.record(z.string(), z.unknown())),
  nextFrom: z.string().nullable(),
  // Present only when the request asked for server-side bucketing
  // (maxPoints). Optional so a frontend built ahead of the API deploy —
  // and short unbucketed ranges — still parse.
  bucketMs: z.number().int().positive().optional(),
  sampleCount: z.number().int().nonnegative().optional(),
  truncated: z.boolean().optional(),
});
export type HistoryResponse = z.infer<typeof HistoryResponse>;
